import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import type { MealSlot, SuggestionDto } from '@fewerbags/schema';
import { api, queryKeys } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { reasonChipClass, SLOT_LABEL, topReasons } from '../../lib/plans';
import { RecipePhoto } from '../recipes/RecipePhoto';
import { Alert } from '../Alert';

/**
 * Recipe suggestions for this meal (PLAN §4.2). The score never reaches the
 * screen — only the reasons, because the explanation is what makes the
 * suggestion trustworthy.
 */
export function SuggestionsPanel({
  date,
  slot,
  windowDays = 3,
  excludeIds = [],
  onAdd,
  busyRecipeId,
}: {
  date: string;
  slot: MealSlot;
  windowDays?: number;
  excludeIds?: readonly string[];
  /** Passed the whole suggestion (not just its id) so the caller can render an
   *  optimistic recipe card before the server confirms the add. */
  onAdd: (suggestion: SuggestionDto) => void;
  busyRecipeId?: string | null;
}) {
  const suggestions = useQuery({
    queryKey: queryKeys.plans.suggestions(date, slot, windowDays),
    queryFn: () => api.plans.suggestions({ date, slot, windowDays, limit: 8 }),
    staleTime: 60_000,
  });

  const items = (suggestions.data?.suggestions ?? []).filter(
    (s) => !excludeIds.includes(s.recipeId),
  );

  return (
    <section aria-labelledby="plan-suggestions-heading" className="card p-4">
      <h2 id="plan-suggestions-heading" className="text-base font-semibold">
        What goes well here
      </h2>
      <p className="mt-1 text-sm text-muted">
        Ideas for this {SLOT_LABEL[slot].toLowerCase()}, based on what is already planned and what
        is in the pantry.
      </p>

      {suggestions.isPending ? <p className="mt-3 text-sm text-muted">Thinking…</p> : null}
      {suggestions.isError ? (
        <Alert tone="error" className="mt-3">
          {messageForError(suggestions.error)}
        </Alert>
      ) : null}
      {suggestions.isSuccess && items.length === 0 ? (
        <p className="mt-3 text-sm text-muted">
          No suggestions yet — they get better once there are more recipes and a stocked pantry.
        </p>
      ) : null}

      <ul className="mt-3 flex flex-col gap-3">
        {items.map((s) => (
          <li key={s.recipeId} className="rounded-lg border border-line p-2">
            <div className="flex gap-3">
              <RecipePhoto
                photoMediaId={s.photoMediaId}
                title={s.title}
                variant="thumb"
                className="size-14 rounded-lg"
              />
              <div className="min-w-0 flex-1">
                <h3 className="text-sm leading-snug">
                  <Link
                    to={`/recipes/${s.recipeId}`}
                    className="underline-offset-2 hover:underline"
                  >
                    {s.title}
                  </Link>
                  {!s.own ? (
                    <span className="ml-2 rounded-full bg-accent-soft px-2 py-0.5 text-xs font-normal text-accent-ink">
                      Shared
                    </span>
                  ) : null}
                </h3>
                <ul className="mt-1 flex flex-wrap gap-1">
                  {topReasons(s).map((reason, i) => (
                    <li
                      key={`${reason.kind}-${i}`}
                      className={`rounded-full px-2 py-0.5 text-xs ${reasonChipClass(reason.delta)}`}
                    >
                      {reason.text}
                    </li>
                  ))}
                </ul>
              </div>
              <button
                type="button"
                onClick={() => onAdd(s)}
                disabled={busyRecipeId === s.recipeId}
                className="min-h-11 shrink-0 self-start rounded-lg border border-line px-3 text-sm font-semibold text-brand disabled:opacity-50"
              >
                {busyRecipeId === s.recipeId ? 'Adding…' : 'Add'}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
